/**
 * IndexedDB Notification Repository Implementation
 *
 * Implements INotificationRepository using Dexie.js for IndexedDB storage.
 * Features:
 * - Ownership-based routing (user → tech, tech → user)
 * - Smart grouping within 5-minute windows
 * - Deduplication by activityId + recipientId
 * - Optimized compound indexes for filtering
 */

import { db } from '../../db';
import type { INotificationRepository } from '../interfaces/INotificationRepository';
import type {
  Notification,
  NotificationGroup,
  RecipientType,
  NotificationType,
  CreateNotificationDTO,
  UpdateNotificationDTO,
  NotificationFilterOptions,
} from '../../../types/notification';
import {
  NotificationPriority,
  generateGroupKey,
  getNotificationPriority,
} from '../../../types/notification';

export class IndexedDBNotificationRepository implements INotificationRepository {
  /**
   * Get all notifications for a recipient.
   * Returns sorted by createdAt descending (newest first).
   */
  async getByRecipient(recipientId: string, recipientType: RecipientType): Promise<Notification[]> {
    const notifications = await db.notifications
      .where('[recipientId+recipientType]')
      .equals([recipientId, recipientType])
      .toArray();

    return notifications.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  /**
   * Get unread notifications for a recipient.
   */
  async getUnread(recipientId: string, recipientType: RecipientType): Promise<Notification[]> {
    const notifications = await db.notifications
      .where('[recipientId+recipientType]')
      .equals([recipientId, recipientType])
      .filter((n) => !n.isRead)
      .toArray();

    return notifications.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  /**
   * Get unread count for a recipient.
   * Used for badge display.
   */
  async getUnreadCount(recipientId: string, recipientType: RecipientType): Promise<number> {
    return db.notifications
      .where('[recipientId+recipientType]')
      .equals([recipientId, recipientType])
      .filter((n) => !n.isRead)
      .count();
  }

  /**
   * Get notifications for a specific ticket.
   */
  async getByTicketId(ticketId: string, recipientId: string): Promise<Notification[]> {
    const notifications = await db.notifications
      .where('ticketId')
      .equals(ticketId)
      .filter((n) => n.recipientId === recipientId)
      .toArray();

    return notifications.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  /**
   * Get a single notification by ID.
   */
  async getById(id: string): Promise<Notification | null> {
    const notification = await db.notifications.get(id);
    return notification ?? null;
  }

  /**
   * Create a new notification.
   */
  async create(data: CreateNotificationDTO): Promise<Notification> {
    const now = new Date();
    const priority = data.priority ?? getNotificationPriority(data.type);

    const notification: Notification = {
      id: crypto.randomUUID(),
      recipientId: data.recipientId,
      recipientType: data.recipientType,
      ticketId: data.ticketId,
      ticketNumber: data.ticketNumber,
      activityId: data.activityId,
      type: data.type,
      priority,
      title: data.title,
      message: data.message,
      isRead: false,
      groupKey: generateGroupKey(data.ticketId, now),
      createdAt: now,
      readAt: null,
      actorId: data.actorId,
      actorName: data.actorName,
      assignmentGroup: data.assignmentGroup,
      newStatus: data.newStatus,
    };

    await db.notifications.add(notification);
    return notification;
  }

  /**
   * Create multiple notifications (batch).
   * Used when one activity triggers notifications to multiple recipients.
   */
  async createMany(data: CreateNotificationDTO[]): Promise<Notification[]> {
    const now = new Date();

    const notifications: Notification[] = data.map((dto) => {
      const priority = dto.priority ?? getNotificationPriority(dto.type);

      return {
        id: crypto.randomUUID(),
        recipientId: dto.recipientId,
        recipientType: dto.recipientType,
        ticketId: dto.ticketId,
        ticketNumber: dto.ticketNumber,
        activityId: dto.activityId,
        type: dto.type,
        priority,
        title: dto.title,
        message: dto.message,
        isRead: false,
        groupKey: generateGroupKey(dto.ticketId, now),
        createdAt: now,
        readAt: null,
        actorId: dto.actorId,
        actorName: dto.actorName,
        assignmentGroup: dto.assignmentGroup,
        newStatus: dto.newStatus,
      };
    });

    await db.notifications.bulkAdd(notifications);
    return notifications;
  }

  /**
   * Update a notification.
   */
  async update(id: string, data: UpdateNotificationDTO): Promise<Notification | null> {
    const existing = await this.getById(id);
    if (!existing) {
      return null;
    }

    const updatedNotification: Notification = {
      ...existing,
      isRead: data.isRead ?? existing.isRead,
      readAt: data.readAt !== undefined ? data.readAt : existing.readAt,
    };

    await db.notifications.put(updatedNotification);
    return updatedNotification;
  }

  /**
   * Mark a notification as read.
   */
  async markAsRead(id: string): Promise<Notification | null> {
    const existing = await this.getById(id);
    if (!existing) {
      return null;
    }

    if (existing.isRead) {
      return existing;
    }

    const updated: Notification = {
      ...existing,
      isRead: true,
      readAt: new Date(),
    };

    await db.notifications.put(updated);
    return updated;
  }

  /**
   * Mark all notifications as read for a recipient.
   * Returns the number of notifications marked as read.
   */
  async markAllAsRead(recipientId: string, recipientType: RecipientType): Promise<number> {
    const unread = await this.getUnread(recipientId, recipientType);

    if (unread.length === 0) {
      return 0;
    }

    const now = new Date();
    const updates = unread.map((n) => ({
      ...n,
      isRead: true,
      readAt: now,
    }));

    await db.notifications.bulkPut(updates);
    return updates.length;
  }

  /**
   * Delete a notification.
   */
  async delete(id: string): Promise<boolean> {
    const existing = await this.getById(id);
    if (!existing) {
      return false;
    }
    await db.notifications.delete(id);
    return true;
  }

  /**
   * Delete all notifications for a ticket (cleanup).
   * Used when a ticket is deleted.
   */
  async deleteByTicketId(ticketId: string): Promise<number> {
    const count = await db.notifications.where('ticketId').equals(ticketId).count();

    await db.notifications.where('ticketId').equals(ticketId).delete();
    return count;
  }

  /**
   * Delete notifications older than a certain date.
   * Used for cleanup/retention policies.
   */
  async deleteOlderThan(date: Date): Promise<number> {
    const oldNotifications = await db.notifications
      .filter((n) => new Date(n.createdAt).getTime() < date.getTime())
      .toArray();

    const ids = oldNotifications.map((n) => n.id);
    await db.notifications.bulkDelete(ids);
    return ids.length;
  }

  /**
   * Get grouped notifications for display.
   * Groups by ticket within time windows (smart grouping).
   */
  async getGrouped(
    recipientId: string,
    recipientType: RecipientType,
    options?: {
      limit?: number;
      includeRead?: boolean;
    }
  ): Promise<NotificationGroup[]> {
    const limit = options?.limit ?? 50;
    const includeRead = options?.includeRead ?? true;

    // Get notifications
    let notifications = await this.getByRecipient(recipientId, recipientType);

    // Filter out read if requested
    if (!includeRead) {
      notifications = notifications.filter((n) => !n.isRead);
    }

    // Group by groupKey
    const groupMap = new Map<string, Notification[]>();

    for (const notification of notifications) {
      const existing = groupMap.get(notification.groupKey);
      if (existing) {
        existing.push(notification);
      } else {
        groupMap.set(notification.groupKey, [notification]);
      }
    }

    // Convert to NotificationGroup array
    const groups: NotificationGroup[] = [];

    for (const [groupKey, groupNotifications] of groupMap) {
      // Sort within group by priority (high first), then by createdAt (newest first)
      groupNotifications.sort((a, b) => {
        const priorityOrder = { high: 0, normal: 1, low: 2 };
        const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
        if (priorityDiff !== 0) return priorityDiff;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });

      const primaryNotification = groupNotifications[0];
      const hasUnread = groupNotifications.some((n) => !n.isRead);

      // Find latest timestamp in group
      const latestAt = groupNotifications.reduce((latest, n) => {
        const nTime = new Date(n.createdAt).getTime();
        return nTime > latest.getTime() ? new Date(n.createdAt) : latest;
      }, new Date(0));

      // Generate summary
      const summary = this.generateGroupSummary(groupNotifications);

      groups.push({
        groupKey,
        ticketId: primaryNotification.ticketId,
        ticketNumber: primaryNotification.ticketNumber,
        primaryNotification,
        notifications: groupNotifications,
        count: groupNotifications.length,
        hasUnread,
        latestAt,
        summary,
      });
    }

    // Sort groups by latestAt (newest first)
    groups.sort((a, b) => b.latestAt.getTime() - a.latestAt.getTime());

    // Apply limit
    return groups.slice(0, limit);
  }

  /**
   * Generate a summary message for a notification group.
   */
  private generateGroupSummary(notifications: Notification[]): string {
    if (notifications.length === 1) {
      return notifications[0].message;
    }

    const count = notifications.length;
    const ticketNumber = notifications[0].ticketNumber;

    // Check if there are high-priority notifications
    const highPriority = notifications.filter(
      (n) => n.priority === NotificationPriority.HIGH
    );

    if (highPriority.length > 0) {
      const firstHighPriority = highPriority[0];
      if (count === 2) {
        return `${firstHighPriority.title} and 1 other update`;
      }
      return `${firstHighPriority.title} and ${count - 1} other updates`;
    }

    return `${count} updates on ${ticketNumber}`;
  }

  /**
   * Check if a notification already exists (for deduplication).
   * Prevents duplicate notifications for the same activity.
   */
  async exists(activityId: string, recipientId: string): Promise<boolean> {
    const existing = await db.notifications
      .where('[activityId+recipientId]')
      .equals([activityId, recipientId])
      .first();

    return existing !== undefined;
  }

  /**
   * Get notifications by type.
   */
  async getByType(
    recipientId: string,
    recipientType: RecipientType,
    type: NotificationType
  ): Promise<Notification[]> {
    const notifications = await db.notifications
      .where('[recipientId+recipientType]')
      .equals([recipientId, recipientType])
      .filter((n) => n.type === type)
      .toArray();

    return notifications.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  /**
   * Get recent notifications (for polling).
   * Returns notifications created after the given timestamp.
   */
  async getRecent(
    recipientId: string,
    recipientType: RecipientType,
    since: Date
  ): Promise<Notification[]> {
    const sinceTime = since.getTime();

    const notifications = await db.notifications
      .where('[recipientId+recipientType]')
      .equals([recipientId, recipientType])
      .filter((n) => new Date(n.createdAt).getTime() > sinceTime)
      .toArray();

    return notifications.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  /**
   * Get notifications with advanced filtering.
   */
  async getFiltered(filters: NotificationFilterOptions): Promise<Notification[]> {
    let query = db.notifications.toCollection();

    // Start with recipient filter if provided
    if (filters.recipientId && filters.recipientType) {
      query = db.notifications
        .where('[recipientId+recipientType]')
        .equals([filters.recipientId, filters.recipientType]);
    }

    let notifications = await query.toArray();

    // Apply additional filters
    if (filters.types && filters.types.length > 0) {
      notifications = notifications.filter((n) => filters.types!.includes(n.type));
    }

    if (filters.isRead !== undefined) {
      notifications = notifications.filter((n) => n.isRead === filters.isRead);
    }

    if (filters.dateRange) {
      const startTime = filters.dateRange.start.getTime();
      const endTime = filters.dateRange.end.getTime();
      notifications = notifications.filter((n) => {
        const nTime = new Date(n.createdAt).getTime();
        return nTime >= startTime && nTime <= endTime;
      });
    }

    // Sort by createdAt descending
    notifications.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    // Apply limit
    if (filters.limit) {
      notifications = notifications.slice(0, filters.limit);
    }

    return notifications;
  }
}
